import type { CavernPropTemplate } from '../../../../shared/scene/cavernPropTemplates';
import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';

/** An opaque roof contact opens into a tapering taproot and separated strands.
 * All branches stay inside the frame; the air between them is real alpha. */
function hangingRoot(p: TownPropPalette): string {
  return `<g data-part="hanging-root">
    <path d="M20 0H62L57 20L52 30L54 51L48 73L47 98L40 126L38 158L30 189
      L31 153L32 121L37 94L35 71L39 48L32 23Z" fill="${hex(p.wood)}" />
    <path d="M42 0H55L48 29L49 51L43 76L43 98L36 125L34 158L30 189
      L31 153L32 121L37 94L35 71L39 48L32 23Z" fill="${hex(p.shade)}" />
    <path d="M23 0H33L29 21L21 40L23 60L17 85L14 111L8 127L11 104L12 82L17 59L14 39L21 18Z"
      fill="${hex(p.wood)}" />
    <path d="M54 0H63L66 22L60 43L63 61L56 89L58 110L53 129L53 108L51 88L57 60L54 42L60 22Z"
      fill="${hex(p.wood)}" />
    <path d="M45 65L51 82L66 97L70 118L66 139L67 118L61 101L45 87Z" fill="${hex(p.wood)}" />
    <path d="M35 111L29 129L19 144L16 163L17 143L25 126L30 107Z" fill="${hex(p.wood)}" />
    <path d="M25 5L35 27L42 43L40 62 M44 82L41 102L36 122L35 143 M23 29L18 41L21 58
      M61 8L63 21L57 40 M50 83L63 99L67 116 M25 131L19 146"
      fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.7" stroke-linecap="round" opacity=".78" />
    <path d="M37 14L43 19L40 25 M40 52L46 57L42 65 M38 92L42 95L38 103"
      fill="none" stroke="${hex(p.iron)}" stroke-width="2" stroke-linecap="round" />
    <path d="M38 159Q45 174 40 190 M15 87Q5 91 5 106 M62 63Q73 73 73 88"
      fill="none" stroke="${hex(p.wood)}" stroke-width="1.8" stroke-linecap="round" />
  </g>`;
}

/** Three separated mineral teeth have a common roof seam. Their uneven tips
 * and narrow facets read as geology without becoming a solid ceiling curtain. */
function stalactite(p: TownPropPalette): string {
  return `<g data-part="ceiling-stalactite">
    <path d="M8 0H64V10L52 17L37 13L25 16L8 10Z" fill="${hex(p.stone)}" />
    <path d="M10 9L27 10L23 40L21 98L16 70Z" fill="${hex(p.primary)}" />
    <path d="M19 12L27 10L23 40L21 98L19 69Z" fill="${hex(p.shade)}" />
    <path d="M28 7L48 5L44 37L39 140L32 95Z" fill="${hex(p.primary)}" />
    <path d="M39 10L48 5L44 37L39 140L37 82Z" fill="${hex(p.shade)}" />
    <path d="M49 9L63 8L60 39L56 88L52 46Z" fill="${hex(p.primary)}" />
    <path d="M56 11L63 8L60 39L56 88L56 44Z" fill="${hex(p.roof)}" />
    <path d="M12 11L17 35L18 64 M31 11L34 46L36 82 M51 14L54 38"
      fill="none" stroke="${hex(p.stone)}" stroke-width="2" stroke-linecap="round" />
    <path d="M14 6L25 9L35 5L47 8L60 4 M34 21L38 27 M17 45L20 49 M55 24L58 28"
      fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.2" opacity=".7" />
    <path d="M31 54L35 59L34 68 M52 48L56 51" fill="none" stroke="${hex(p.iron)}" stroke-width="1.5" />
  </g>`;
}

/** Uneven lobed bulbs, bent stalks and an opaque root footing.
 * Light-looking detail is limited to the bulb skins; no surrounding halo. */
function sporePod(p: TownPropPalette): string {
  return `<g data-part="spore-pod">
    <path d="M12 67L23 62Q33 49 29 32L35 29Q40 47 34 54L39 62L53 67L57 72H8Z" fill="${hex(p.wood)}" />
    <path d="M18 66Q22 53 13 42 M41 65Q43 50 51 42"
      fill="none" stroke="${hex(p.woodEdge)}" stroke-width="3" stroke-linecap="round" />
    <path d="M23 66Q37 53 32 35 M39 65L35 55 M13 70H25 M40 70H52"
      fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.5" stroke-linecap="round" />
    <path d="M27 31C21 30 20 25 23 21C17 17 20 9 25 9C24 4 32 1 35 6
      C41 3 46 8 43 13C49 18 43 25 39 27L36 33Z" fill="${hex(p.glass)}" />
    <path d="M26 27Q33 24 43 19Q41 26 36 33L27 31Z" fill="${hex(p.primary)}" />
    <path d="M27 25Q27 29 31 31 M32 24Q31 28 34 30 M38 22Q34 26 36 29"
      fill="none" stroke="${hex(p.roofEdge)}" stroke-width="1.1" stroke-linecap="round" />
    <path d="M24 13Q25 10 28 11 M28 7Q31 6 32 8" fill="none"
      stroke="${hex(p.glassCore)}" stroke-width="1.7" stroke-linecap="round" opacity=".8" />
    <ellipse cx="29" cy="17" rx="1.6" ry="1.1" fill="${hex(p.glassCore)}" opacity=".75" />
    <ellipse cx="37" cy="12" rx="1.1" ry="1.5" fill="${hex(p.glassCore)}" opacity=".65" />
    <path d="M9 43C3 40 2 35 6 32C3 28 7 24 12 26C15 22 22 26 21 30
      C25 34 19 40 15 42L13 45Z" fill="${hex(p.glass)}" />
    <path d="M6 38Q13 38 21 32Q21 39 15 42L13 45L9 43Z" fill="${hex(p.primary)}" />
    <path d="M9 38Q10 41 13 42 M15 36Q13 39 15 40" fill="none"
      stroke="${hex(p.roofEdge)}" stroke-width="1.1" stroke-linecap="round" />
    <ellipse cx="9" cy="30" rx="1.9" ry="1.1" fill="${hex(p.glassCore)}" opacity=".8" />
    <ellipse cx="16" cy="29" rx="1.1" ry="1.1" fill="${hex(p.glassCore)}" opacity=".7" />
    <path d="M46 43C41 40 43 34 47 32C45 28 50 23 54 27C60 25 63 31 60 35
      C64 40 57 45 52 44L49 47Z" fill="${hex(p.glass)}" />
    <path d="M44 39Q51 41 61 36Q62 42 52 44L49 47L46 43Z" fill="${hex(p.primary)}" />
    <path d="M47 39Q47 42 50 44 M53 39Q50 42 52 43" fill="none"
      stroke="${hex(p.roofEdge)}" stroke-width="1.1" stroke-linecap="round" />
    <ellipse cx="51" cy="30" rx="1.7" ry="1.1" fill="${hex(p.glassCore)}" opacity=".8" />
    <ellipse cx="56" cy="33" rx="1.1" ry="1.5" fill="${hex(p.glassCore)}" opacity=".7" />
    <path d="M25 66L32 63L38 67L46 68L50 72H18Z" fill="${hex(p.primary)}" />
    <path d="M26 68H31 M36 70H42" stroke="${hex(p.stone)}" stroke-width="1.3" />
  </g>`;
}

/** Narrow broken trunk with an open fork and uneven side branches. Every
 * painted region belongs behind the playable terrain through its catalog band. */
function rootColumn(p: TownPropPalette): string {
  return `<g data-part="hollow-root-column">
    <path d="M28 288L46 271L52 232L49 195L59 156L54 118L62 82L52 51L55 9L63 37L73 57
      L84 33L92 3L94 39L81 76L83 112L76 152L81 190L78 230L89 270L112 288Z" fill="${hex(p.wood)}" />
    <path d="M67 79L65 118L69 154L62 194L67 235L62 270L48 288H78L76 267L72 232L76 190
      L71 152L77 111L73 77L88 38L90 15L81 41Z" fill="${hex(p.shade)}" />
    <path d="M60 112L39 91L20 85L7 68L24 77L44 80L67 96Z" fill="${hex(p.wood)}" />
    <path d="M79 87L102 67L128 58L137 45L132 65L108 78L81 104Z" fill="${hex(p.wood)}" />
    <path d="M56 164L32 145L17 144L5 132L27 136L45 143L64 150Z" fill="${hex(p.wood)}" />
    <path d="M78 155L99 130L114 126L132 111L121 132L104 141L80 176Z" fill="${hex(p.wood)}" />
    <path d="M78 208L100 193L118 189L137 177L124 197L103 205L77 224Z" fill="${hex(p.wood)}" />
    <path d="M49 269L30 273L12 288H41L57 276 M84 270L109 275L133 288H103L82 279"
      fill="${hex(p.wood)}" />
    <path d="M57 35L65 60L69 77 M62 91L59 117L64 149L57 176 M54 198L59 229L55 260
      M85 47L77 72 M78 183L74 208L75 238L83 267 M44 279L31 285 M98 280L115 285
      M20 82L40 87L57 104 M102 73L124 64 M34 143L54 155 M102 138L120 128 M99 201L121 194"
      fill="none" stroke="${hex(p.woodEdge)}" stroke-width="2.2" stroke-linecap="round" opacity=".78" />
    <path d="M60 215Q54 197 60 184Q65 174 69 187Q74 202 68 217Z" fill="${hex(p.iron)}" />
    <path d="M60 211Q58 194 63 187" fill="none" stroke="${hex(p.primary)}" stroke-width="1.5" />
    <path d="M62 130L70 135L66 145 M55 249L62 253L60 260 M76 111L80 118L76 125"
      fill="none" stroke="${hex(p.iron)}" stroke-width="2.2" stroke-linecap="round" />
    <path d="M36 287H52 M90 287H111" stroke="${hex(p.woodEdge)}" stroke-width="1.3" />
  </g>`;
}

export function buildCavernPropBody(template: CavernPropTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'hanging_root': return hangingRoot(palette);
  case 'ceiling_stalactite': return stalactite(palette);
  case 'spore_pod': return sporePod(palette);
  case 'hollow_root_column': return rootColumn(palette);
  }
}
